/**
 * Client mirror of constants/markdownTables.js — pipe tables to HTML.
 */

function escapeHtml(value: string) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function inlineMarkdown(value: string) {
  return escapeHtml(value)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

function linesOf(text: string) {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function splitRow(line: string) {
  let raw = String(line || "").trim();
  if (raw.startsWith("|")) raw = raw.slice(1);
  if (raw.endsWith("|")) raw = raw.slice(0, -1);
  return raw.split("|").map((cell) => cell.trim());
}

function isSeparatorCell(cell: string) {
  return /^:?-{3,}:?$/.test(String(cell || "").replace(/\s/g, ""));
}

function isSeparatorRow(line: string) {
  const cells = splitRow(line);
  return cells.length > 0 && cells.every(isSeparatorCell);
}

function alignFromSeparator(cell: string) {
  const raw = String(cell || "").replace(/\s/g, "");
  const left = raw.startsWith(":");
  const right = raw.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  return "";
}

export function isMarkdownTable(text: string) {
  const lines = linesOf(text);
  if (lines.length < 2) return false;
  if (!isSeparatorRow(lines[1])) return false;
  return lines.every((line) => line.includes("|"));
}

export function markdownTableToHtml(text: string) {
  const lines = linesOf(text);
  if (!isMarkdownTable(text)) return "";
  const headers = splitRow(lines[0]);
  const aligns = splitRow(lines[1]).map(alignFromSeparator);
  const width = headers.length;
  const rows = lines.slice(2).map((line) => {
    const cells = splitRow(line);
    while (cells.length < width) cells.push("");
    return cells.slice(0, width);
  });
  const alignAttr = (i: number) => (aligns[i] ? ` style="text-align:${aligns[i]}"` : "");
  const head = headers
    .map((cell, i) => `<th${alignAttr(i)}>${inlineMarkdown(cell)}</th>`)
    .join("");
  const body = rows
    .map((row) => {
      const tds = row.map((cell, i) => `<td${alignAttr(i)}>${inlineMarkdown(cell)}</td>`).join("");
      return `<tr>${tds}</tr>`;
    })
    .join("");
  return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

/** Turn pipe-table paragraphs (or a raw markdown table) into <table> HTML. */
export function upgradePipeTables(htmlOrMarkdown: string) {
  const raw = String(htmlOrMarkdown || "");
  if (!raw.trim() || !raw.includes("|")) return raw;

  const held: string[] = [];
  const withoutTables = raw.replace(/<table\b[\s\S]*?<\/table>/gi, (block) => {
    held.push(block);
    return `\n%%TABLE${held.length - 1}%%\n`;
  });

  const upgraded = withoutTables.replace(/<p\b[^>]*>([\s\S]*?)<\/p>/gi, (all, inner) => {
    const text = String(inner)
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/&nbsp;/gi, " ")
      .replace(/<[^>]+>/g, "");
    return isMarkdownTable(text) ? markdownTableToHtml(text) : all;
  });

  if (upgraded !== withoutTables) {
    return upgraded.replace(/%%TABLE(\d+)%%/g, (_, n) => held[Number(n)] || "");
  }

  const stripped = withoutTables.replace(/<[^>]+>/g, "");
  if (isMarkdownTable(stripped)) {
    const text = withoutTables
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>\s*<p[^>]*>/gi, "\n")
      .replace(/<\/?p[^>]*>/gi, "\n")
      .replace(/<[^>]+>/g, "");
    return markdownTableToHtml(text);
  }

  return raw;
}

const INGEST_TAGS =
  "p|br|div|span|strong|b|em|i|u|s|sub|sup|mark|h[1-6]|ul|ol|li|dl|dt|dd|blockquote|pre|code|a|table|thead|tbody|tfoot|tr|th|td|caption|hr";

const ESCAPED_TAG_RE = new RegExp(`&lt;\\/?(?:${INGEST_TAGS})(?:\\s|/|&gt;)`, "i");
const DOUBLE_ESCAPED_TAG_RE = /&amp;lt;\/?[a-z]/i;
const ESCAPED_TAG_REPLACE_RE = new RegExp(`&lt;(\\/?)(${INGEST_TAGS})((?:(?!&gt;).)*)&gt;`, "gi");

export function looksLikeEscapedHtml(value: string) {
  const raw = String(value || "");
  return ESCAPED_TAG_RE.test(raw) || DOUBLE_ESCAPED_TAG_RE.test(raw);
}

function decodeAttrEntities(value: string) {
  return String(value || "")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");
}

export function unescapeEncodedMarkup(value: string) {
  let next = String(value || "");
  for (let i = 0; i < 2; i += 1) {
    if (!looksLikeEscapedHtml(next)) break;
    next = next.replace(/&amp;lt;/g, "&lt;").replace(/&amp;gt;/g, "&gt;");
    if (!ESCAPED_TAG_RE.test(next)) continue;
    next = next.replace(ESCAPED_TAG_REPLACE_RE, (_, slash, tag, attrs) => {
      return `<${slash}${tag}${decodeAttrEntities(String(attrs || ""))}>`;
    });
  }
  return next;
}

function quoteLine(line: string) {
  return /^>\s?/.test(String(line || "").trim());
}

export function isMarkdownQuote(text: string) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .filter((line) => line.trim());
  return lines.length > 0 && lines.every(quoteLine);
}

export function markdownQuoteToHtml(text: string) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .filter((line) => line.trim());
  const inner = lines
    .map((line) => inlineMarkdown(line.trim().replace(/^>\s?/, "")))
    .join("<br>");
  return `<blockquote><p>${inner}</p></blockquote>`;
}

function paragraphPlain(inner: string) {
  return String(inner || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/&nbsp;/gi, " ")
    .replace(/&gt;/g, ">")
    .replace(/<[^>]+>/g, "");
}

export function upgradeMarkdownQuotes(html: string) {
  const raw = String(html || "");
  if (!raw.trim() || (!raw.includes(">") && !raw.includes("&gt;"))) return raw;
  return raw.replace(/<p\b[^>]*>([\s\S]*?)<\/p>/gi, (all, inner) => {
    const text = paragraphPlain(String(inner));
    return isMarkdownQuote(text) ? markdownQuoteToHtml(text) : all;
  });
}

export function upgradeAuthorHtml(value: string) {
  let next = unescapeEncodedMarkup(String(value || ""));
  next = upgradePipeTables(next);
  next = upgradeMarkdownQuotes(next);
  return next;
}
